import { Download } from 'lucide-react';
import FileDropZone from '../../../components/common/FileDropZone';
import { Button } from '../../../components/ui/button';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import { cn } from '../../../lib/utils';
import UploadStatusBadge from '../UploadStatusBadge';
import { UPLOAD_ACCEPT, datasetLabel, formatDay, isRequested, overdueLabel } from '../uploadCycle';
import RowFileButton from './RowFileButton';
import UploadedFacts from './UploadedFacts';

interface ObligationRowProps {
  row: SupplierUploadTracking;
  uploading: boolean;
  /** Another row's upload is running — hold this one. */
  disabled: boolean;
  /** The period's due date, already shown in the card header; skip repeating it. */
  periodDueDate?: string | null;
  onUpload: (file: File) => void;
  /** Saves this dataset's header row, right where the file is owed. */
  onDownloadTemplate?: () => void;
}

/**
 * One dataset inside a period card.
 *
 * A file that is owed gets the drop zone and the colour; a file already in
 * gets its figures and a quiet Replace, so the eye lands on the work left.
 */
export default function ObligationRow({
  row,
  uploading,
  disabled,
  periodDueDate,
  onUpload,
  onDownloadTemplate,
}: ObligationRowProps) {
  const uploaded = row.status === 'uploaded';
  const overdue = row.status === 'overdue';
  // The card header carries this month's due date and how late it is, so a row
  // only speaks up when it has something of its own to say.
  const ownDate = row.due_date !== periodDueDate ? row.due_date : null;
  const note = !isRequested(row)
    ? 'Not asked for — send it if you have it'
    : ownDate && `Due ${formatDay(ownDate)}${overdue ? ` · ${overdueLabel(ownDate)}` : ''}`;

  const heading = (
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-medium text-foreground">{datasetLabel(row)}</p>
      <UploadStatusBadge status={row.status} label={row.status_display} />
    </div>
  );

  if (uploaded) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-green-200/70 bg-green-50/40 px-4 py-3 dark:border-green-900/60 dark:bg-green-950/20"
        data-testid={`obligation-${row.dataset_type}`}
      >
        <div className="min-w-0 flex-1 space-y-1">
          {heading}
          <UploadedFacts row={row} />
        </div>
        <RowFileButton row={row} uploading={uploading} disabled={disabled} onUpload={onUpload} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between md:gap-6',
        overdue && 'border-l-4 border-l-red-500',
      )}
      data-testid={`obligation-${row.dataset_type}`}
    >
      <div className="min-w-0 space-y-1">
        {heading}
        {note && (
          <p className={cn('text-sm', overdue ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
            {note}
          </p>
        )}
        {onDownloadTemplate && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground"
            onClick={onDownloadTemplate}
            data-testid={`row-template-${row.dataset_type}`}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Download the {datasetLabel(row)} template
          </Button>
        )}
      </div>

      {/* Wide enough that the standard prompt stays on one line, so the row keeps its height. */}
      <div className="w-full md:w-[26rem] md:shrink-0">
        <FileDropZone
          size="compact"
          accept={UPLOAD_ACCEPT}
          busy={uploading}
          disabled={disabled}
          hint="CSV or Excel"
          onFiles={(files) => onUpload(files[0])}
          aria-label={`Upload ${datasetLabel(row)} file`}
          data-testid={`upload-${row.dataset_type}-${row.id}`}
        />
      </div>
    </div>
  );
}
